import { ChevronRight, Mail, Plus, SendIcon } from 'assets/icons';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import { companyRoleLabel } from 'constants/companyRoles';
import useText from 'hooks/useText';
import { LIST_GROUP_HEADER_CLASS } from 'utils/surfaceClasses';
import ContactRow from './ContactRow';
import { RecordSection } from './RecordLayout';

// The header of a group of contacts, across the whole card as the groups of
// the home page's tasks: the first one takes the card's top corners.
const GroupHead = ({ first, children }) => (
  <p className={`-mx-4 border-b border-gray-100 ${LIST_GROUP_HEADER_CLASS} ${first ? '-mt-4 rounded-t-xl' : 'border-t'}`}>{children}</p>
);

/**
 * The « Contacts » of a company, as the admin's tab: its members registered
 * on Jobbiz (an invitation pending can be sent again), then the people of
 * HubSpot without an account (they are invited); each opens their page. Then
 * the contacts of its group, read only: they are managed on the group.
 */
const CompanyContacts = ({ members, hubspot, group, groupContacts, onOpen, onAdd, onInvite, onResend, onOpenGroup, onOpenGroupContact }) => {
  const { text, language } = useText('record');
  const detail = (person) =>
    [
      person.position,
      person.account === 'active' && person.role && text('access', { role: companyRoleLabel(language, person.role) }),
      person.phone,
      person.email,
    ]
      .filter(Boolean)
      .join(' · ');
  const count = members.length + hubspot.length;
  return (
    <div className="flex flex-col gap-4 pb-6">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900">{text('contactsCount', { count })}</p>
          <Button color="SECONDARY" size="SM" onClick={onAdd}>
            <Plus wh={18} color="currentColor" />
            <span>{text('addContact')}</span>
          </Button>
        </div>
        <RecordSection>
          {!count && <CompactEmptyState compact glyph="inbox" title={text('contactsEmptyTitle')} description={text('contactsEmptyText')} />}
          {members.length > 0 && (
            <div>
              <GroupHead first>{text('registered', { count: members.length })}</GroupHead>
              {members.map((person) => (
                <ContactRow
                  key={person.id}
                  person={person}
                  detail={detail(person)}
                  onOpen={() => onOpen(person)}
                  actions={
                    person.account === 'pending' && (
                      <IconTooltip label={text('resend')}>
                        <button type="button" aria-label={text('resend')} onClick={() => onResend(person)} className={cardIconButtonClass}>
                          <Mail wh={18} color="#374151" />
                        </button>
                      </IconTooltip>
                    )
                  }
                />
              ))}
            </div>
          )}
          {hubspot.length > 0 && (
            <div>
              <GroupHead first={!members.length}>{text('notRegistered', { count: hubspot.length })}</GroupHead>
              {hubspot.map((person) => (
                <ContactRow
                  key={person.id}
                  person={person}
                  detail={detail(person)}
                  onOpen={() => onOpen(person)}
                  actions={
                    <Button color="SECONDARY" size="SM" onClick={() => onInvite(person)}>
                      <SendIcon wh={16} color="currentColor" />
                      <span>{text('invite')}</span>
                    </Button>
                  }
                />
              ))}
            </div>
          )}
        </RecordSection>
      </div>
      {group && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900">{text('groupContacts', { count: groupContacts.length })}</p>
            <Button color="SECONDARY" size="SM" onClick={onOpenGroup}>
              <span>{group.name}</span>
              <ChevronRight wh={16} color="currentColor" />
            </Button>
          </div>
          <RecordSection>
            {!groupContacts.length && <p className="text-sm text-gray-600">{text('groupContactsEmpty')}</p>}
            {groupContacts.map((person) => (
              <ContactRow
                key={person.id}
                person={person}
                detail={[person.position, person.phone, person.email].filter(Boolean).join(' · ')}
                onOpen={() => onOpenGroupContact(person)}
              />
            ))}
          </RecordSection>
        </div>
      )}
    </div>
  );
};

export default CompanyContacts;
